import React, { useCallback, useEffect, useEffectEvent, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactSelect, { components as rs } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Icon from '../Icon';
import InfoTooltip from '../Icon/InfoTooltip/Index';
import Chip, { CHIP_SIZE } from '../Chip';
import { useTranslations } from '@components/context/TranslationContext';
import SelectDropdownNative from './SelectDropdownNative';
import { describeField } from '../foundations/fieldDescription';
import {
    SELECT_DROPDOWN_CREATE_TAG_TERM,
    SELECT_DROPDOWN_DEFAULT_CREATE_TAG_FORMAT,
    SELECT_DROPDOWN_ICON,
    SELECT_DROPDOWN_MENU_MAX_WIDTH,
    SELECT_DROPDOWN_PORTAL_Z_INDEX,
    SELECT_DROPDOWN_SIZE,
    SELECT_DROPDOWN_SIZES,
    SELECT_DROPDOWN_SEARCH_DEBOUNCE_MS,
    SELECT_DROPDOWN_VARIANT,
    SELECT_DROPDOWN_VARIANTS,
} from './constants';
import {
    isSameLabel,
    isSameValue,
    matchesTerm,
    normalizeOptions,
    normalizeValue,
    resolveSelection,
    shouldCloseMenuOnScroll,
    toGroupedOptions,
    toOptionData,
} from './helpers';

// Design-system select (design: Select & dropdown / Form fields cards), built
// on react-select (npm) in unstyled mode — react-select provides the menu,
// keyboard, portal and multi/creatable machinery; the DS look comes from the
// `ds-select-dropdown` classes styled in index.less. Controlled: value in,
// onChange(value, { data }) out — the legacy select2 payload shape, so
// select2-era consumers can migrate without reshaping handlers. `native`
// renders a real <select> with the same API instead. Presentational: an async
// source feeds options/loading/loadFailed/onSearch (see @hooks/useRemoteOptions).
// All UI text goes through
// t() and can be overridden via `labels`. Styles ship via less/ats_style.less
// (white-label pipeline) — no JSX .less import, per frontend-structure.mdc.

// react-select custom parts (module-level: a stable identity avoids remounts).
const Option = (props) => {
    const { data, isSelected, children } = props;
    const { dsRenderOption: renderOption, dsActiveIcon: activeIcon } = props.selectProps;
    // activeIcon: true → the check, false → nothing, a string → that core/Icon name.
    const activeIconName = typeof activeIcon === 'string' ? activeIcon : SELECT_DROPDOWN_ICON.CHECK;
    const activeMark = isSelected && activeIcon !== false && (
        <Icon value={activeIconName} noMargin className="ds-select-dropdown__option-check" />
    );
    // Consumer-rendered rows (e.g. avatar + name + email) replace the icon/label/description
    // trio; the active mark still renders so the row keeps the menu's selection semantics.
    // The row names its value for anything that addresses options by value rather than label.
    // Additive only: react-select's own handlers and ids on innerProps are kept as they are.
    const innerProps = data.value == null
        ? props.innerProps
        : { ...props.innerProps, 'data-value': String(data.value) };
    if (renderOption && !data.__isNew__) {
        return (
            <rs.Option {...props} innerProps={innerProps}>
                <span className="ds-select-dropdown__option-custom">{renderOption(data)}</span>
                {activeMark}
            </rs.Option>
        );
    }
    return (
        <rs.Option {...props} innerProps={innerProps}>
            {data.__isNew__ ? (
                <Icon value={SELECT_DROPDOWN_ICON.CREATE} noMargin className="ds-select-dropdown__option-icon" />
            ) : data.icon ? (
                <Icon value={data.icon} noMargin className="ds-select-dropdown__option-icon" />
            ) : null}
            {props.selectProps.dsAllowHtml && typeof children === 'string' ? (
                // Legacy allowHtml (select2 escapeMarkup) parity — labels are
                // trusted backend markup, same as before the migration.
                <span className="ds-select-dropdown__option-label" dangerouslySetInnerHTML={{ __html: children }} />
            ) : (
                <span className="ds-select-dropdown__option-label">{children}</span>
            )}
            {data.description && <span className="ds-select-dropdown__option-suffix">{data.description}</span>}
            {activeMark}
        </rs.Option>
    );
};

// react-select builds its own aria-describedby for the input and overwrites a
// passed-in one, so the field's error/helper id is appended to it here.
const Input = (props) => {
    const described = [props['aria-describedby'], props.selectProps.dsDescribedBy].filter(Boolean).join(' ');
    return <rs.Input {...props} aria-describedby={described || undefined} />;
};

const DropdownIndicator = (props) => (
    <rs.DropdownIndicator {...props}>
        <Icon value={SELECT_DROPDOWN_ICON.CHEVRON} noMargin />
    </rs.DropdownIndicator>
);

const ClearIndicator = (props) => (
    <rs.ClearIndicator
        {...props}
        innerProps={{ ...props.innerProps, 'aria-label': props.selectProps.dsLabels.clear, role: 'button' }}
    >
        <Icon value={SELECT_DROPDOWN_ICON.CLEAR} noMargin />
    </rs.ClearIndicator>
);

// Selected values render as core/Chip. mousedown must not bubble into the
// control (it would toggle the menu while removing a chip).
const MultiValue = ({ data, removeProps, selectProps }) => (
    <span
        className="ds-select-dropdown__chip"
        onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
        }}
    >
        <Chip
            size={selectProps.dsChipSize}
            value={<span className="ds-select-dropdown__chip-label">{data.label}</span>}
            disabled={selectProps.isDisabled}
            onRemove={selectProps.isDisabled ? undefined : removeProps.onClick}
            removeTooltip={`${selectProps.dsLabels.remove} ${data.label}`}
        />
    </span>
);

// The loading state renders as a menu row (loadingMessage); the animated
// indicator dots rely on emotion styles that unstyled mode strips.
const LoadingIndicator = () => null;

const SingleValue = (props) =>
    props.selectProps.dsAllowHtml && typeof props.children === 'string' ? (
        <rs.SingleValue {...props}>
            <span dangerouslySetInnerHTML={{ __html: props.children }} />
        </rs.SingleValue>
    ) : (
        <rs.SingleValue {...props} />
    );

const RS_COMPONENTS = { Input, Option, DropdownIndicator, ClearIndicator, MultiValue, SingleValue, LoadingIndicator };

const RS_CLASSNAMES = {
    container: () => 'ds-select-dropdown__container',
    control: ({ isFocused, isDisabled, menuIsOpen }) =>
        classNames('ds-select-dropdown__control', {
            'ds-select-dropdown__control--focused': isFocused,
            'ds-select-dropdown__control--open': menuIsOpen,
            'ds-select-dropdown__control--disabled': isDisabled,
        }),
    valueContainer: ({ isMulti }) => classNames('ds-select-dropdown__value-container', { 'ds-select-dropdown__chips': isMulti }),
    placeholder: () => 'ds-select-dropdown__placeholder',
    singleValue: () => 'ds-select-dropdown__value',
    input: () => 'ds-select-dropdown__inline-input',
    indicatorsContainer: () => 'ds-select-dropdown__indicators',
    indicatorSeparator: () => 'ds-select-dropdown__indicator-separator',
    dropdownIndicator: () => 'ds-select-dropdown__chevron',
    clearIndicator: () => 'ds-select-dropdown__clear',
    menu: () => 'ds-select-dropdown__menu',
    menuPortal: () => 'ds-select-dropdown__menu-portal',
    menuList: () => 'ds-select-dropdown__options',
    group: () => 'ds-select-dropdown__group-section',
    groupHeading: () => 'ds-select-dropdown__group',
    option: ({ isFocused, isSelected, isDisabled, data }) =>
        classNames('ds-select-dropdown__option', {
            'ds-select-dropdown__option--active': isFocused,
            'ds-select-dropdown__option--selected': isSelected,
            'ds-select-dropdown__option--disabled': isDisabled,
            'ds-select-dropdown__option--create': Boolean(data.__isNew__),
        }),
    noOptionsMessage: () => 'ds-select-dropdown__status',
    loadingMessage: () => 'ds-select-dropdown__status',
};

// The portal renders on document.body with react-select's inline positioning,
// so the z-index has to travel inline too (see SELECT_DROPDOWN_PORTAL_Z_INDEX).
// Menu geometry also goes inline: unstyled mode still emits `width: 100%` on
// the menu, which would otherwise race our stylesheet on specificity.
const buildRsStyles = (menuWidth) => ({
    menuPortal: (base) => ({ ...base, zIndex: SELECT_DROPDOWN_PORTAL_Z_INDEX }),
    menu: (base) => ({
        ...base,
        width: 'max-content',
        minWidth: '100%',
        maxWidth: typeof menuWidth === 'number' ? `${menuWidth}px` : menuWidth,
    }),
});

const SelectDropdown = ({
    options: optionsProp = [],
    value = null,
    onChange = () => {},
    multiple = false,
    tags = false,
    searchable = true,
    native = false,
    portal = true,
    size = SELECT_DROPDOWN_SIZE.LG,
    variant = SELECT_DROPDOWN_VARIANT.DEFAULT,
    menuPlacement = 'auto',
    menuWidth = SELECT_DROPDOWN_MENU_MAX_WIDTH,
    label,
    placeholder,
    leadingIcon,
    helperText,
    error = false,
    required = false,
    disabled = false,
    loading = false,
    loadFailed = false,
    allowClear = false,
    onSearch,
    minSearchLength = 0,
    maxSelections = 0,
    createTagFormat = SELECT_DROPDOWN_DEFAULT_CREATE_TAG_FORMAT,
    labels = {},
    id,
    name,
    className,
    onFocus,
    onBlur,
    onOpen,
    onClose,
    tooltip,
    clearOnChange = false,
    resetValue = false,
    allowHtml = false,
    renderOption,
    activeIcon = true,
    ref,
}) => {
    const { t } = useTranslations();
    const autoId = useId();
    const [term, setTerm] = useState('');
    const rsRef = useRef(null);
    const nativeRef = useRef(null);
    const resolvedSize = SELECT_DROPDOWN_SIZES.has(size) ? size : SELECT_DROPDOWN_SIZE.LG;
    const resolvedVariant = SELECT_DROPDOWN_VARIANTS.has(variant)
        ? variant
        : SELECT_DROPDOWN_VARIANT.DEFAULT;
    const isSoft = resolvedVariant === SELECT_DROPDOWN_VARIANT.SOFT;
    const chipSize = CHIP_SIZE.SM;

    const text = {
        empty: labels.empty || t('No results found'),
        loading: labels.loading || t('Loading...'),
        clear: labels.clear || t('Clear selection'),
        remove: labels.remove || t('Remove'),
        minSearch: labels.minSearch || t('Type at least {count} characters to search', { count: minSearchLength }),
        maxSelected: labels.maxSelected || t('You can only select {max} items', { max: maxSelections }),
        loadFailed: labels.loadFailed || t('Could not load the options. Please try again.'),
    };

    const rsStyles = useMemo(() => buildRsStyles(menuWidth), [menuWidth]);
    const normalized = useMemo(() => normalizeOptions(optionsProp), [optionsProp]);
    const grouped = useMemo(() => toGroupedOptions(normalized), [normalized]);
    const values = useMemo(() => normalizeValue(value, multiple, normalized), [value, multiple, normalized]);
    const selection = useMemo(
        () => (resetValue ? [] : resolveSelection(values, normalized)),
        [resetValue, values, normalized],
    );

    const atMax = multiple && maxSelections > 0 && values.length >= maxSelections;
    const belowMin = useCallback(
        (input) => minSearchLength > 0 && (input || '').trim().length < minSearchLength,
        [minSearchLength],
    );

    const comboboxId = id || `${autoId}-select-dropdown`;

    const handleChange = useCallback(
        (picked) => {
            if (multiple) {
                const list = picked || [];
                onChange(list.map((option) => option.value), { data: list.map(toOptionData) });
                return;
            }
            if (!picked) {
                onChange(null, { data: null });
                return;
            }
            onChange(picked.value, { data: toOptionData(picked) });
        },
        [multiple, onChange],
    );

    const handleInputChange = useCallback((input, meta) => {
        if (meta.action === 'input-change') setTerm(input);
        else if (meta.action === 'menu-close') setTerm('');
    }, []);

    // Debounced async source: the consumer pairs onSearch with options+loading.
    // The empty mount term is not a search: with no minimum length `belowMin('')`
    // is false, so firing on the first run would send every consumer an
    // unrequested full-list request on each mount.
    const searchedRef = useRef(false);
    const hasSearch = typeof onSearch === 'function';
    const fireSearch = useEffectEvent((searchTerm) => onSearch?.(searchTerm));
    useEffect(() => {
        if (!hasSearch || belowMin(term)) return undefined;
        if (!searchedRef.current && term === '') {
            searchedRef.current = true;
            return undefined;
        }
        searchedRef.current = true;
        const timer = setTimeout(() => fireSearch(term), SELECT_DROPDOWN_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [term, hasSearch, belowMin]);

    const filterOption = useCallback(
        (candidate, input) => {
            if (belowMin(input)) return false;
            // With an onSearch source the server filters.
            if (onSearch) return true;
            return matchesTerm(candidate.label, input);
        },
        [belowMin, onSearch],
    );

    const isOptionDisabled = useCallback(
        (option) => option.disabled || (atMax && !values.some((v) => isSameValue(v, option.value))),
        [atMax, values],
    );

    const noOptionsMessage = useCallback(
        ({ inputValue }) => {
            if (belowMin(inputValue)) return text.minSearch;
            if (atMax) return text.maxSelected;
            return loadFailed ? text.loadFailed : text.empty;
        },
        [belowMin, atMax, loadFailed, text.minSearch, text.maxSelected, text.empty, text.loadFailed],
    );

    const isValidNewOption = useCallback(
        (input, selectedList, availableOptions) => {
            const trimmed = (input || '').trim();
            if (!tags || atMax || !trimmed) return false;
            // The user types a LABEL, so matching only on value offers "create
            // Design" next to an existing { value: 12, label: 'Design' }.
            const matches = (option) => isSameValue(option.value, trimmed) || isSameLabel(option.label, trimmed);
            const existsInOptions = availableOptions.some((option) =>
                option.options ? option.options.some(matches) : matches(option),
            );
            const existsInSelection = selectedList.some(matches);
            return !existsInOptions && !existsInSelection;
        },
        [tags, atMax],
    );

    const errorMessage = typeof error === 'string' ? error : null;
    const { errorId, helperId, describedBy } = describeField(comboboxId, {
        errorText: errorMessage,
        helperText,
    });

    const rootClasses = classNames(
        'ds-select-dropdown',
        `ds-select-dropdown--${resolvedSize}`,
        `ds-select-dropdown--${resolvedVariant}`,
        {
            'ds-select-dropdown--disabled': disabled,
            'ds-select-dropdown--error': Boolean(error),
            'ds-select-dropdown--multiple': multiple,
            'ds-select-dropdown--native': native,
        },
        className,
    );

    const labelContent = label != null && (
        <>
            {label}
            {required && (
                <span className="ds-select-dropdown__required" aria-hidden>
                    *
                </span>
            )}
            {tooltip && <InfoTooltip value={tooltip} />}
        </>
    );

    // Soft variant renders the label INSIDE the control box (softbox design);
    // the default variant keeps it above the control.
    const renderLabel = () =>
        !isSoft && labelContent && (
            <label className="ds-select-dropdown__label" htmlFor={comboboxId}>
                {labelContent}
            </label>
        );

    const renderInnerLabel = () =>
        isSoft && labelContent && (
            <label className="ds-select-dropdown__inner-label" htmlFor={comboboxId}>
                {labelContent}
            </label>
        );

    // Legacy imperative handle — same method names as legacy/core/Select2, so
    // ref-based call sites migrate without changes.
    useImperativeHandle(ref, () => ({
        focusInput: () => {
            if (native) {
                nativeRef.current?.focus();
                return;
            }
            rsRef.current?.focus();
            rsRef.current?.openMenu?.('first');
        },
        clearValue: () => onChange(multiple ? [] : null, { data: null }),
    }), [native, multiple, onChange]);

    const closeMenuOnAncestorScroll = useCallback(shouldCloseMenuOnScroll, []);

    const renderMessages = () => (
        <>
            {errorMessage && (
                <div className="ds-select-dropdown__error-message" id={errorId}>
                    {errorMessage}
                </div>
            )}
            {helperId && (
                <div className="ds-select-dropdown__help" id={helperId}>
                    {helperText}
                </div>
            )}
        </>
    );

    const renderLeadIcon = () =>
        leadingIcon && (
            <span className="ds-select-dropdown__lead-icon" aria-hidden>
                <Icon value={leadingIcon} noMargin />
            </span>
        );

    if (native) {
        // A real <select> has no menu of ours to search, create in, or show a
        // spinner over, so these are accepted-and-ignored rather than broken.
        if (process.env.NODE_ENV !== 'production') {
            const ignored = Object.entries({ tags, loading, onSearch })
                .filter(([, supplied]) => Boolean(supplied))
                .map(([name]) => name)
                .concat(maxSelections > 0 ? ['maxSelections'] : []);
            if (ignored.length) {
                // eslint-disable-next-line no-console
                console.warn(`SelectDropdown: native mode ignores ${ignored.join(', ')}`);
            }
        }
        return (
            <div className={rootClasses}>
                {renderLabel()}
                <div className="ds-select-dropdown__control-wrap">
                    {renderInnerLabel()}
                    {renderLeadIcon()}
                    <SelectDropdownNative
                        options={normalized}
                        value={value}
                        onChange={onChange}
                        multiple={multiple}
                        disabled={disabled}
                        required={required}
                        placeholder={placeholder}
                        allowClear={allowClear}
                        id={comboboxId}
                        name={name}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        selectRef={nativeRef}
                        ariaInvalid={Boolean(error) || undefined}
                        ariaDescribedBy={describedBy}
                    />
                    {!multiple && (
                        <span className="ds-select-dropdown__chevron" aria-hidden>
                            <Icon value={SELECT_DROPDOWN_ICON.CHEVRON} noMargin />
                        </span>
                    )}
                </div>
                {renderMessages()}
            </div>
        );
    }

    const SelectComponent = tags ? CreatableSelect : ReactSelect;

    return (
        <div className={rootClasses}>
            {renderLabel()}
            <div className="ds-select-dropdown__control-wrap">
                {renderInnerLabel()}
                {renderLeadIcon()}
                <SelectComponent
                    ref={rsRef}
                    inputId={comboboxId}
                    instanceId={comboboxId}
                    aria-invalid={Boolean(error) || undefined}
                    dsDescribedBy={describedBy}
                    name={name}
                    unstyled
                    classNames={RS_CLASSNAMES}
                    styles={rsStyles}
                    components={RS_COMPONENTS}
                    dsLabels={text}
                    dsChipSize={chipSize}
                    dsAllowHtml={allowHtml}
                    dsRenderOption={renderOption}
                    dsActiveIcon={activeIcon}
                    options={grouped}
                    value={multiple ? selection : selection[0] ?? null}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    getOptionValue={(option) => String(option.value)}
                    getOptionLabel={(option) => option.label}
                    isMulti={multiple}
                    isDisabled={disabled}
                    isLoading={loading}
                    isClearable={allowClear}
                    isSearchable={searchable}
                    required={required}
                    placeholder={placeholder ?? ''}
                    filterOption={filterOption}
                    isOptionDisabled={isOptionDisabled}
                    noOptionsMessage={noOptionsMessage}
                    loadingMessage={() => text.loading}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onMenuOpen={onOpen}
                    onMenuClose={onClose}
                    menuPortalTarget={portal && typeof document !== 'undefined' ? document.body : undefined}
                    menuPosition={portal ? 'fixed' : 'absolute'}
                    closeMenuOnScroll={portal ? closeMenuOnAncestorScroll : undefined}
                    menuPlacement={menuPlacement}
                    closeMenuOnSelect={!multiple}
                    controlShouldRenderValue={!clearOnChange}
                    backspaceRemovesValue={!clearOnChange}
                    hideSelectedOptions={false}
                    tabSelectsValue={false}
                    {...(tags
                        ? {
                              isValidNewOption,
                              // optionLabel is the formatted create-row text; the
                              // raw term travels as the value (toOptionData swaps
                              // the label back for the payload and the chips).
                              getNewOptionData: (input, optionLabel) => ({
                                  value: (input || '').trim(),
                                  label: optionLabel,
                                  __isNew__: true,
                              }),
                              formatCreateLabel: (input) =>
                                  createTagFormat.replace(SELECT_DROPDOWN_CREATE_TAG_TERM, (input || '').trim()),
                              createOptionPosition: 'last',
                          }
                        : {})}
                />
            </div>
            {renderMessages()}
        </div>
    );
};

SelectDropdown.propTypes = {
    /** Options: `{value,label,icon,description,disabled}`, bare strings/numbers,
     * groups `{label, options: []}` — or the legacy select2 shapes `{id,text}`
     * and `{text, children: []}` */
    options: PropTypes.array,
    /** Selected value (single) or array of values (multiple). Controlled. */
    value: PropTypes.any,
    /** Called with (value, { data }) — data mirrors the legacy select2 payload:
     * the selected option object (single) or all selected options (multiple) */
    onChange: PropTypes.func,
    /** Multi-select: chips in the control, toggle-select in the menu */
    multiple: PropTypes.bool,
    /** Free-text creation — the term becomes a selectable "create" row */
    tags: PropTypes.bool,
    /** Filterable options (typing in the control filters the menu) */
    searchable: PropTypes.bool,
    /** Render a browser-native select with the same API */
    native: PropTypes.bool,
    /** Control size: sm (30px) / md (33px) / lg (36px, default). The menu keeps
     * the one design look; the size scales the field, icons and text. */
    size: PropTypes.oneOf(Object.values(SELECT_DROPDOWN_SIZE)),
    /** Control skin: 'default' (label above the box) or 'soft' — the softbox
     * design with the label INSIDE the field (56/52/48px per size). Both skins
     * share the same menu. */
    variant: PropTypes.oneOf(Object.values(SELECT_DROPDOWN_VARIANT)),
    /** Portal the menu to document.body so overflow:hidden ancestors —
     * widget cards, modals — can't clip it */
    portal: PropTypes.bool,
    /** Where the menu opens: 'auto' (default — flips up when space below runs
     * out), 'bottom', or 'top' */
    menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
    /** Menu width cap: the menu grows to fit its options up to this
     * (number → px, or any CSS width). Default 480px; long labels ellipsize. */
    menuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Field label above the control */
    label: PropTypes.node,
    /** Shown while nothing is selected */
    placeholder: PropTypes.string,
    /** core/Icon name shown at the start of the control */
    leadingIcon: PropTypes.string,
    /** Hint under the control (hidden while an error message shows) */
    helperText: PropTypes.node,
    /** Error state; a string also renders as the message under the control */
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    /** Marks the label with * and the control required */
    required: PropTypes.bool,
    /** Disables the whole control */
    disabled: PropTypes.bool,
    /** Shows the loading row instead of options (async sources) */
    loading: PropTypes.bool,
    /** The async source failed: the empty menu says so instead of "No results found" */
    loadFailed: PropTypes.bool,
    /** Renders a clear ("×") affordance while a value is selected */
    allowClear: PropTypes.bool,
    /** Async source: called (debounced) with the term; pair with `options` + `loading`
     * (`@hooks/useRemoteOptions` provides all of them for a select2-style search url) */
    onSearch: PropTypes.func,
    /** Minimum term length before options/onSearch kick in (select2's minimumInputLength) */
    minSearchLength: PropTypes.number,
    /** Selection cap for multiple mode; 0 = unlimited (select2 semantics) */
    maxSelections: PropTypes.number,
    /** Label template for the create row; `{term}` is replaced with the input */
    createTagFormat: PropTypes.string,
    /** UI string overrides: { empty, loading, clear, remove, minSearch, maxSelected, loadFailed } */
    labels: PropTypes.object,
    /** DOM id for the combobox input (and label target) */
    id: PropTypes.string,
    /** Form field name */
    name: PropTypes.string,
    /** Extra class on the root */
    className: PropTypes.string,
    /** Focus/blur on the control (both custom-menu and native modes) */
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    /** Menu open/close (custom-menu mode; the native picker is browser-owned) */
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    /** InfoTooltip rendered next to the label (legacy Select2 parity) */
    tooltip: PropTypes.node,
    /** Emit onChange but keep the control empty — for "adder" dropdowns that
     * reset after every pick (legacy Select2 parity) */
    clearOnChange: PropTypes.bool,
    /** While true, the control renders as empty regardless of `value`
     * (legacy Select2 parity) */
    resetValue: PropTypes.bool,
    /** Render option/value labels as raw HTML (legacy allowHtml / select2
     * escapeMarkup parity). HARD RULE: only ever set this for labels the
     * BACKEND composes. Never enable it for anything a user can type — a
     * candidate name, a tag, a free-text field — that is stored XSS. Every
     * migration that turns it on must name where the markup comes from. */
    allowHtml: PropTypes.bool,
    /** Custom menu row: `(option) => node`, receives the normalized option
     * (`value`, `label`, `raw` = the original object). The selected check and
     * the create row are still drawn by the component. */
    renderOption: PropTypes.func,
    /** Mark on the selected row: true (default) draws the check, false draws
     * nothing, a core/Icon name draws that icon instead */
    activeIcon: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    /** Imperative handle: `{ focusInput(), clearValue() }` */
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export { SELECT_DROPDOWN_ICON, SELECT_DROPDOWN_SIZE, SELECT_DROPDOWN_MENU_MAX_WIDTH, SELECT_DROPDOWN_VARIANT };
export { default as SelectDropdownNative } from './SelectDropdownNative';
export default SelectDropdown;
